import { getSession } from 'utils/auth'
import { useSatuEventTracker } from 'utils/satuEvent'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { RealisasiSkp } from 'types/kinerja-ks-pelaksanaan'
import type { SETransformAttrsArgs } from 'utils/satuEvent'

export type EventName =
  | 'CLICK_PENGELOLAAN_KINERJA'
  | 'OPEN_KINERJA_HOMEPAGE'
  | 'CLICK_KINERJA_CEK_LANGKAH_OBSERVASI'
  | 'OPEN_KINERJA_PELAKSANAAN_OBSERVASI_HUB'
  | 'CLICK_KINERJA_PELAKSANAAN_ISI_DOKUMEN_PERSIAPAN'
  | 'OPEN_KINERJA_PELAKSANAAN_FORM_A'
  | 'CLICK_KINERJA_PELAKSANAAN_FORM_A_TO_POP_UP'
  | 'OPEN_KINERJA_PELAKSANAAN_FORM_A_POP_UP'
  | 'CLICK_KINERJA_PELAKSANAAN_SUBMIT_FORM_A'
  | 'CLICK_KINERJA_PELAKSANAAN_ISI_DOKUMEN_TINJUT'
  | 'OPEN_KINERJA_PELAKSANAAN_FORM_B_RESULTS'
  | 'CLICK_KINERJA_PELAKSANAAN_TO_FORM_C'
  | 'OPEN_KINERJA_PELAKSANAAN_FORM_C'
  | 'CLICK_KINERJA_PELAKSANAAN_FORM_C_TO_POP_UP'
  | 'OPEN_KINERJA_PELAKSANAAN_FORM_C_POP_UP'
  | 'CLICK_KINERJA_PELAKSANAAN_SUBMIT_FORM_C'
  | 'CLICK_KINERJA_PELAKSANAAN_ISI_DOKUMEN_REFLEKSI_TINJUT'
  | 'OPEN_KINERJA_PELAKSANAAN_FORM_D'
  | 'CLICK_KINERJA_PELAKSANAAN_FORM_D_TO_POP_UP'
  | 'OPEN_KINERJA_PELAKSANAAN_FORM_D_POP_UP'
  | 'CLICK_KINERJA_PELAKSANAAN_SUBMIT_FORM_D'
  | 'CLICK_KINERJA_PELAKSANAAN_KUMPULKAN_DOKUMEN_PENGEMBANGAN_KOMPETENSI'
  | 'OPEN_KINERJA_PELAKSANAAN_PENGEMBANGAN_KOMPETENSI'
  | 'OPEN_KINERJA_PELAKSANAAN_PENGEMBANGAN_KOMPETENSI_UNGGAH_PAGE'
  | 'CLICK_KINERJA_PELAKSANAAN_PENGEMBANGAN_KOMPETENSI_SUBMIT_TO_POP_UP'
  | 'OPEN_KINERJA_PELAKSANAAN_UNGGAH_BUKTI_DUKUNG_POP_UP'
  | 'CLICK_KINERJA_PELAKSANAAN_SUBMIT_BUKTI_DUKUNG'
  | 'CLICK_KINERJA_PELAKSANAAN_SIMPAN_DRAF'

export type ScreenName =
  | 'WEB_HOMEPAGE'
  | 'APP_HOMEPAGE'
  | 'LANDING_PAGE'
  | 'KINERJA_KS_HOMEPAGE'
  | 'KINERJA_KS_PELAKSANAAN_OBSERVASI_HUB_PAGE'
  | 'KINERJA_KS_PELAKSANAAN_FORM_A_PAGE'
  | 'KINERJA_KS_FORM_A_POP_UP'
  | 'KINERJA_KS_PELAKSANAAN_FORM_B_RESULTS_PAGE'
  | 'KINERJA_KS_PELAKSANAAN_FORM_C'
  | 'KINERJA_KS_PELAKSANAAN_FORM_C_PAGE'
  | 'KINERJA_KS_FORM_C_POP_UP'
  | 'KINERJA_KS_PELAKSANAAN_FORM_D'
  | 'KINERJA_KS_PELAKSANAAN_FORM_D_PAGE'
  | 'KINERJA_KS_FORM_D_POP_UP'
  | 'KINERJA_KS_PELAKSANAAN_PENGEMBANGAN_KOMPTENSI_PAGE'
  | 'KINERJA_KS_PELAKSANAAN_PENGEMBANGAN_KOMPETENSI_UNGGAH_PAGE'
  | 'KINERJA_KS_PELAKSANAAN_SUBMIT_BUKTI_DUKUNG_POP_UP'
  | 'KINERJA_KS_PELAKSANAAN_SIMPAN_DRAF_POP_UP'

interface TrackerParams {
  event_name: EventName
  screen_name: ScreenName

  attributes?: SETransformAttrsArgs
}

const SATU_EVENT_NAME = 'ks_skp_pelaksanaan_fe_user_action_v1'

const useSKPKSPelaksanaanTrackEvent = () => {
  const { SETrackEvent, SETransformAttrs } = useSatuEventTracker()
  const session = getSession()

  const {
    isEnabled: isMultiPeriodEnabled,
    isHavePeriod,
    periodYear,
    periodNo,
    isFetched,
  } = usePeriodContext()

  const trackEvent = (params: TrackerParams) => {
    const attributes = params.attributes ?? {}

    if (isMultiPeriodEnabled && isHavePeriod && isFetched) {
      attributes['year'] = [periodYear, 'int']
      attributes['period'] = [periodNo, 'int']
    }

    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      eventParams: {
        ...params,
        attributes: SETransformAttrs(attributes),
        event_trigger: params.screen_name,
        user_id: session?.user?.id ?? '-',
      },
      sendDefaultParams: true,
      // we manually send the user data above
      // to handle case where the user is not logged in
      sendUserData: false,
      sendAttributes: false,
      keepAlive: true,
    })
  }

  const trackOpenKinerjaHomepage = (params: { kinerja_state: string }) => {
    trackEvent({
      event_name: 'OPEN_KINERJA_HOMEPAGE',
      screen_name: 'KINERJA_KS_HOMEPAGE',
      attributes: {
        kinerja_state: params.kinerja_state,
      },
    })
  }

  const trackClickKinerjaCekLangkahObservasi = (params: {
    kinerja_pelaksanaan_id: string
    pelaksanaan_observasi_completion: number
  }) => {
    trackEvent({
      event_name: 'CLICK_KINERJA_CEK_LANGKAH_OBSERVASI',
      screen_name: 'KINERJA_KS_HOMEPAGE',
      attributes: params,
    })
  }

  const trackOpenPelaksanaanObservasiHub = (params: {
    kinerja_pelaksanaan_id: string
    pelaksanaan_observasi_completion: number
    sub_indikator_id: string
    collected_document_status: {
      dokumen_persiapan: boolean
      atasan_observasi: boolean
      dokumen_tindak_lanjut: boolean
      lakukan_tindak_lanjut: boolean
      dokumen_refleksi_tindak_lanjut: boolean
    }
  }) => {
    trackEvent({
      event_name: 'OPEN_KINERJA_PELAKSANAAN_OBSERVASI_HUB',
      screen_name: 'KINERJA_KS_PELAKSANAAN_OBSERVASI_HUB_PAGE',
      attributes: {
        kinerja_pelaksanaan_id: params.kinerja_pelaksanaan_id,
        pelaksanaan_observasi_completion:
          params.pelaksanaan_observasi_completion,
        sub_indikator_id: params.sub_indikator_id,
        collected_document_status: JSON.stringify(
          params.collected_document_status
        ),
      },
    })
  }

  const trackOpenFormA = (params: {
    kinerja_pelaksanaan_id: string
    pelaksanaan_observasi_completion: number
  }) => {
    trackEvent({
      event_name: 'OPEN_KINERJA_PELAKSANAAN_FORM_A',
      screen_name: 'KINERJA_KS_PELAKSANAAN_FORM_A_PAGE',
      attributes: params,
    })
  }

  const trackClickSubmitFormA = (params: {
    kinerja_pelaksanaan_id: string
  }) => {
    trackEvent({
      event_name: 'CLICK_KINERJA_PELAKSANAAN_SUBMIT_FORM_A',
      screen_name: 'KINERJA_KS_FORM_A_POP_UP',
      attributes: params,
    })
  }

  const trackOpenFormBResults = (params: {
    kinerja_pelaksanaan_id: string
    pelaksanaan_observasi_completion: number
  }) => {
    trackEvent({
      event_name: 'OPEN_KINERJA_PELAKSANAAN_FORM_B_RESULTS',
      screen_name: 'KINERJA_KS_PELAKSANAAN_FORM_B_RESULTS_PAGE',
      attributes: params,
    })
  }

  const trackOpenFormC = (params: { kinerja_pelaksanaan_id: string }) => {
    trackEvent({
      event_name: 'OPEN_KINERJA_PELAKSANAAN_FORM_C',
      screen_name: 'KINERJA_KS_PELAKSANAAN_FORM_C',
      attributes: params,
    })
  }

  const trackClickKinerjaSubmitFormC = (params: {
    kinerja_pelaksanaan_id: string
  }) => {
    trackEvent({
      event_name: 'CLICK_KINERJA_PELAKSANAAN_SUBMIT_FORM_C',
      screen_name: 'KINERJA_KS_FORM_C_POP_UP',
      attributes: params,
    })
  }

  const trackOpenFormD = (params: { kinerja_pelaksanaan_id: string }) => {
    trackEvent({
      event_name: 'OPEN_KINERJA_PELAKSANAAN_FORM_D',
      screen_name: 'KINERJA_KS_PELAKSANAAN_FORM_D',
      attributes: params,
    })
  }

  const trackClickKinerjaPelaksanaanSubmitFormD = (params: {
    kinerja_pelaksanaan_id: string
  }) => {
    trackEvent({
      event_name: 'CLICK_KINERJA_PELAKSANAAN_SUBMIT_FORM_D',
      screen_name: 'KINERJA_KS_FORM_D_POP_UP',
      attributes: params,
    })
  }

  const trackClickKinerjaPelaksanaanPengembanganKompetensi = (params: {
    kinerja_pelaksanaan_id: string
    pelaksanaan_observasi_completion: number
  }) => {
    trackEvent({
      event_name:
        'CLICK_KINERJA_PELAKSANAAN_KUMPULKAN_DOKUMEN_PENGEMBANGAN_KOMPETENSI',
      screen_name: 'KINERJA_KS_HOMEPAGE',
      attributes: params,
    })
  }

  const trackClickKinerjaPelaksanaanSubmitBuktiDukung = (params: {
    kinerja_pelaksanaan_id: string
    rhk_id: RealisasiSkp['kontenId']
    bukti_dukung_id: string
  }) => {
    trackEvent({
      event_name: 'CLICK_KINERJA_PELAKSANAAN_SUBMIT_BUKTI_DUKUNG',
      screen_name: 'KINERJA_KS_PELAKSANAAN_SUBMIT_BUKTI_DUKUNG_POP_UP',
      attributes: params,
    })
  }

  const trackClickKinerjaPelaksanaanSimpanDraf = (params: {
    kinerja_pelaksanaan_id: string
    screen_name: ScreenName
    rhk_id?: RealisasiSkp['kontenId']
    bukti_dukung_id?: string
  }) => {
    trackEvent({
      event_name: 'CLICK_KINERJA_PELAKSANAAN_SIMPAN_DRAF',
      screen_name: params.screen_name,
      attributes: {
        kinerja_pelaksanaan_id: params.kinerja_pelaksanaan_id,
        rhk_id: params.rhk_id,
        bukti_dukung_id: params.bukti_dukung_id,
      },
    })
  }

  return {
    trackEvent,

    trackOpenKinerjaHomepage,
    trackClickKinerjaCekLangkahObservasi,
    trackOpenPelaksanaanObservasiHub,

    trackOpenFormD,
    trackClickKinerjaPelaksanaanSubmitFormD,

    trackClickKinerjaPelaksanaanPengembanganKompetensi,
    trackClickKinerjaPelaksanaanSubmitBuktiDukung,

    trackClickKinerjaPelaksanaanSimpanDraf,
    trackOpenFormA,
    trackClickSubmitFormA,

    trackOpenFormBResults,

    trackOpenFormC,
    trackClickKinerjaSubmitFormC,
  }
}

export default useSKPKSPelaksanaanTrackEvent
